/**
 * The one way a service frontend reads the signed-in user's Firebase ID token: off the SHARED bridge
 * (`window.__federaFirebase`, published by the host shell's or SPA's own AuthProvider).
 *
 * Going through a locally-imported Firebase SDK instead would stand up a SECOND Auth instance inside the service
 * bundle and disturb the shell's, so every gated MFE/SPA client reaches its bearer through here.
 *
 * HOME. It sits beside the flow-dispatch transport it credentials (`components/lib/api/`), not in `tools/`: it is
 * page-context-only code, so no flow leaf can import it and none ever did, and not in `utils/`, whose floor admits only
 * `@federa/const`, `@federa/types` and `zod` (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist) -- a `utils/` home
 * would mean respelling `isBrowser` inline, which is exactly the duplication that floor exists to prevent.
 */

import { publishedFirebaseBridge } from '@federa/const/shared-window-state';

/**
 * Resolve the bearer PER REQUEST, never captured once: an ID token expires in ~1h and `getIdToken()` refreshes it
 * transparently.
 *
 * `authStateReady()` is awaited before reading `currentUser` because a mount-time call otherwise races session
 * restoration and reads null, dropping the bearer off the first render's fetches.
 *
 * `forceRefresh` mints a NEW token instead of returning the SDK's cached one. Pass it on RETRY: a cached token the SDK
 * will not refresh on its own (expired, or an auth instance left bad by an earlier session) otherwise makes every retry
 * present the identical rejected credential -- an unbreakable reconnect loop only a log-out/log-in clears. Default
 * false, so an ordinary per-request read is unchanged.
 *
 * A null return is NOT a short-circuit signal: callers send the request bare and let the worker answer 401, because a
 * client-side auth verdict is advisory and duplicating it only adds a second place for it to drift.
 *
 * THIS LEAF DOES NOT SWALLOW: a `getIdToken` rejection PROPAGATES, and the catch boundary is the CALLER's, because the
 * two live dispositions disagree and only the caller knows which applies. A one-shot fetch client wants the rejection
 * (the request never goes out and its own error path reports it); a reconnect loop wants "no credential" plus a log
 * line, which `readFirebaseIdToken` (`spa/chats/frontend/src/assistant/ws-credential.ts`) supplies at its call site.
 * Catching here would erase the first disposition and there would be nowhere left to restore it.
 *
 * SIBLING, deliberately NOT folded: `readWsAuthToken` (`utils/websocket/ws-auth-token.ts`) answers the same question for
 * the storage-gateway WS identity gate under a `utils/` import floor this module could not satisfy, and its catch is
 * SILENT by contract (the SDK message can carry token detail that must not reach a log).
 */
export async function readBridgeIdToken(options: { forceRefresh?: boolean } = {}): Promise<string | null> {
	const bridge = publishedFirebaseBridge();
	if (!bridge) return null;
	await bridge.auth.authStateReady();
	const currentUser = bridge.auth.currentUser;
	if (!currentUser) return null;
	return currentUser.getIdToken(options.forceRefresh);
}
